'use strict';
import classNames from 'classnames';
import get from 'lodash/get';
import isNumber from 'lodash/isNumber';
import * as T from 'prop-types';
import React from 'react';
import {Swipeout} from '../../../_libs/rc-swipeout';
import {IonSpinner} from '../../../_libs/reactionic/src/components/ionSpinner';
import {CommentIndicatorComponent} from '../../../comments/components/CommentIndicator/CommentIndicatorComponent';
import {Button} from '../../../components/buttons/Button';
import {ListItem} from '../../../components/lists/ListItem';
import {StatelessStatusSelector} from '../../../core-components/selectors/components/StatelessStatusSelector';
import {getAsString, getCollection, getId} from '../../../core-utilities/domain/ReduxUtils';
import {getItemTitle, stopPropagation} from '../../../core-utilities/react-utils';


// see https://github.com/fedosejev/react-file-drag-and-drop for example of using the onDrop events

export function RequirementItemView(props) {
  const model = props.model;

  return (
    <ListItem
      className={props.itemClassName}
      bindParam={model}
      onClick={props.onClick}
      onContextMenu={props.onContextMenu}
    >
      <Swipeout
        className={classNames(
          "RequirementItem",
          props.className,
        )}
        autoClose={true}
        disabled={!props.enableSwipeout}
        right={RequirementItemView.getSwipeoutRight(props)}
      >
        {renderContent(props)}
      </Swipeout>
    </ListItem>
  );
}

function renderContent(props) {
  const model = props.model;
  const itemTitle = getItemTitle(model);

  const name = getAsString(model);
  const description = model.description;

  return (
    <div
      className={classNames("RequirementItem__content", {
        RequirementItem__content_isDragging: props.isDragging,
      })}
      onDragEnter={props.onDragEnter}
      onDragLeave={props.onDragLeave}
      onDragOver={props.onDragOver}
      onDrop={props.onDrop}
    >
      <div className="RequirementItem__slideItem">
        <div className={classNames(
          "RequirementItem__innerContent", {
            'double-line': description,
          },
        )}
        >
          <div
            className="RequirementItem__left"
            title={getUploadsCountTitle(props)}
            onClick={stopPropagation}
          >
            {props.uploadCount > 0 ? (
              <IonSpinner
                className="RequirementItem__spinner"
                icon="spiral"
              />
            ) : (
              <StatelessStatusSelector
                className="RequirementItem__status"
                labelClassName=""
                model={props.model}

                hideValue={true}

                status={props.model.status}
                collection={props.model.collection}
                validStatuses={props.model.validStatuses}
              />
            )}
          </div>

          <h2
            className={classNames({
              RequirementItem__title_single: !description,
            })}
            title={itemTitle}
          >
            {name}
          </h2>
          <span
            className="item-description"
            title={itemTitle}
          >
            {description || ' '}
          </span>

          <div className="RequirementItem__right">
            <div className="RequirementItem__metrics">
              <i className="icon ion-ios-folder-outline" />
              <span
                className="RequirementItem__metrics__label"
                title={getFileCountTitle(props)}
              >
                {get(model.metrics, 'fileUploadedCount', 0)}
              </span>
            </div>

            <CommentIndicatorComponent
              className="RequirementItem__chat"
              userId={props.userId}
              chatId={getId(props.model)}
              collection={getCollection(props.model)}
              allowNav={false}
              init={props.init}
            />
          </div>
        </div>
      </div>

      <div
        className="RequirementItem__slideItemOptions"
        onClick={stopPropagation}
      >
        <div className="filter-flex">
          {props.showUploadButton
            ? (
              <Button
                className={classNames("BaseButton IconButton", {
                  'RequirementItem__sideButton_disabled': !props.enableUploadButton,
                })}
                iconClass="icon ion-android-upload"
                title={props.filesHeaderLabel}
                onClick={props.onUploadButtonClick}
              />
            ) : null}
          {props.showIOSUpButton
            ? (
              <Button
                className={classNames("BaseButton IconButton", {
                  'RequirementItem__sideButton_disabled': !props.enableIOSUpButton,
                })}
                iconClass="icon ion-ios-photos"
                title={props.filesHeaderLabel}
                onClick={props.onUpIOSButtonClick}
              />
            ) : null}
          {props.showCameraButton
            ? (
              <Button
                className={classNames("BaseButton IconButton", {
                  'RequirementItem__sideButton_disabled': !props.enableCameraButton,
                })}
                iconClass="icon ion-ios-camera-outline"
                title={props.photosHeaderLabel}
                onClick={props.onCameraButtonClick}
              />
            ) : null}
          {props.showVideoButton
            ? (
              <Button
                className={classNames("BaseButton IconButton", {
                  'RequirementItem__sideButton_disabled': !props.enableVideoButton,
                })}
                iconClass="icon ion-ios-videocam"
                title={props.photosHeaderLabel}
                onClick={props.onVideoButtonClick}
              />
            ) : null}
        </div>
      </div>
    </div>
  );
}

RequirementItemView.propTypes = {
  className: T.string,
  itemClassName: T.string,

  model: T.shape({
    name: T.string.isRequired,
    description: T.string,
  }),
  uploadCount: T.number.isRequired,
  uploadCountTitle: T.string,
  fileCountTitle: T.string,
  filesHeaderLabel: T.string,
  photosHeaderLabel: T.string,

  onClick: T.func.isRequired,
  onContextMenu: T.func.isRequired,
  showCameraButton: T.bool.isRequired,
  onCameraButtonClick: T.func.isRequired,
  showVideoButton: T.bool.isRequired,
  onVideoButtonClick: T.func.isRequired,
  showUploadButton: T.bool.isRequired,
  onUploadButtonClick: T.func.isRequired,
  showIOSUpButton: T.bool.isRequired,
  onUpIOSButtonClick: T.func.isRequired,
  enableCameraButton: T.bool.isRequired,
  enableVideoButton: T.bool.isRequired,
  enableUploadButton: T.bool.isRequired,
  enableIOSUpButton: T.bool.isRequired,

  enableSwipeout: T.bool.isRequired,
  onDragEnter: T.func.isRequired,
  onDragLeave: T.func.isRequired,
  onDragOver: T.func.isRequired,
  onDrop: T.func.isRequired,
  isDragging: T.bool.isRequired,

  init: T.bool,
};

RequirementItemView.defaultProps = {
  uploadCountTitle: '',
  fileCountTitle: '',
  filesHeaderLabel: '',
  photosHeaderLabel: '',
  enableCameraButton: true,
  enableVideoButton: true,
  enableUploadButton: true,
  enableIOSUpButton: true,

  init: true,
};

RequirementItemView.getSwipeoutRight = props => {
  const options = [];
  if (props.showUploadButton) {
    options.push({
      button: <i className="icon ion-android-upload" />,
      onClick: props.onUploadButtonClick,
      className: classNames('button button-positive', {
        'RequirementItem__sideButton_disabled': !props.enableUploadButton,
      }),
    });
  }

  if (props.showIOSUpButton) {
    options.push({
      button: <i className="icon ion-ios-photos-outline" />,
      onClick: props.onUpIOSButtonClick,
      className: classNames('button button-calm', {
        'RequirementItem__sideButton_disabled': !props.enableIOSUpButton,
      }),
    });
  }

  if (props.showCameraButton) {
    options.push({
      button: <i className="icon ion-ios-camera-outline" />,
      onClick: props.onCameraButtonClick,
      className: classNames('button button-assertive', {
        'RequirementItem__sideButton_disabled': !props.enableCameraButton,
      }),
    });
  }

  if (props.showVideoButton) {
    options.push({
      button: <i className="icon ion-ios-videocam-outline" />,
      onClick: props.onVideoButtonClick,
      className: classNames('button button-royal', {
        'RequirementItem__sideButton_disabled': !props.enableVideoButton,
      }),
    });
  }

  return options;
};

RequirementItemView.messages = [
  {code: 'requirements.files.item.has.uploads.title', prop: 'uploadCountTitle'},
  {code: 'requirements.files.item.file.title', prop: 'fileCountTitle'},
  {code: 'requirements.files.header.label', prop: 'filesHeaderLabel'},
  {code: 'fulfillments.photos.header.label', prop: 'photosHeaderLabel'},
];

RequirementItemView.messageProps = [
  'uploadCountTitle',
  'fileCountTitle',
  'filesHeaderLabel',
  'photosHeaderLabel',
];

function getUploadsCountTitle(props) {
  if (isNumber(props.uploadCount) && props.uploadCount > 0) {
    return `${props.uploadCountTitle} ${props.uploadCount}`;
  } else {
    return '';
  }
}

function getFileCountTitle(props) {
  const count = get(props.model.metrics, 'fileUploadedCount', 0);
  return `${props.fileCountTitle} ${count}`;
}



// WEBPACK FOOTER //
// ./src/js/requirements/components/RequirementItem/RequirementItemView.js